import { billDisplayName } from '@orn/shared-web/lib/bill/billDisplayName';
import { bankLogoSrc } from '@orn/shared-web/utils/bankLogoSrc';

import { WorkItemStatus } from '../../__generated__/globalTypes';

import type { WorkItemDetailsFragment } from '../../components/alerts/__generated__/workItemFragment.gql';
import type { GetWorkItemStatusCountsQuery } from '../../hooks/queries/__generated__/useWorkItemStatusCounts.gql';

/**
 * The alert row model's own vocabulary. `work_items` is the only wire source —
 * `workItemToListedAlert` folds a fragment row into this shape and the mock
 * factories compose it directly — so these unions carry exactly the values
 * that fold can produce, not the wider set the retired wire type once did.
 */
export const ALERT_ROW_STATUSES = ['UNREAD', 'READ', 'PENDING', 'ARCHIVED'] as const;

/** Lifecycle state of an alert row, folded from the work item's `(status, read)`. */
export type AlertRowStatus = (typeof ALERT_ROW_STATUSES)[number];

export const ALERT_ROW_TYPES = ['UPDATE', 'REMINDER', 'CONFIG'] as const;

/** Intent of an alert row, from the work item's user category. */
export type AlertRowType = (typeof ALERT_ROW_TYPES)[number];

/** The condition a `CONFIG` row wants fixed. Adapted work items carry `null`. */
export const ALERT_ROW_SUB_TYPES = ['UNVERIFIED_PHONE', 'UNHEALTHY_BANK_CONNECTION'] as const;

export type AlertRowSubType = (typeof ALERT_ROW_SUB_TYPES)[number];

/** Status group an alert is listed under in the Alerts Center. */
export type AlertStatusSection = 'needAttention' | 'pending' | 'history';

/** A new `AlertRowStatus` does not compile until it picks a section. */
export const ALERT_SECTION_BY_STATUS: Record<AlertRowStatus, AlertStatusSection> = {
  UNREAD: 'needAttention',
  READ: 'needAttention',
  PENDING: 'pending',
  ARCHIVED: 'history',
};

/** Top-to-bottom order the sections render in. */
export const ALERT_STATUS_SECTIONS: readonly AlertStatusSection[] = ['needAttention', 'pending', 'history'];

/** Every lifecycle status, for a read that wants the whole list rather than one section. */
export const ALL_ALERT_STATUSES: readonly AlertRowStatus[] = ALERT_ROW_STATUSES;

function groupStatusesBySection(): Record<AlertStatusSection, readonly AlertRowStatus[]> {
  const grouped: Record<AlertStatusSection, AlertRowStatus[]> = { needAttention: [], pending: [], history: [] };

  for (const status of ALL_ALERT_STATUSES) {
    grouped[ALERT_SECTION_BY_STATUS[status]].push(status);
  }

  return grouped;
}

/**
 * The statuses each section's list filters to. Folded from
 * {@link ALERT_SECTION_BY_STATUS} rather than written out, so a new
 * `AlertRowStatus` reaches the Alerts Center under the section it already
 * picked instead of going unlisted.
 */
export const ALERT_STATUSES_BY_SECTION: Record<AlertStatusSection, readonly AlertRowStatus[]> =
  groupStatusesBySection();

/**
 * The statuses an alert is still *active* in: the user has something to do with
 * it. `PENDING` waits on someone else and `ARCHIVED` is closed, so a surface
 * showing only what needs attention — the assistant composer's strip — filters
 * to these.
 *
 * Derived from the Alerts Center's need-attention section rather than restated,
 * so the two always agree on both the status values and their order.
 */
export const ACTIVE_ALERT_STATUSES: readonly AlertRowStatus[] = ALERT_STATUSES_BY_SECTION.needAttention;

/** Section headers uppercase these through CSS, so the source strings stay sentence case. */
export const ALERT_SECTION_LABELS: Record<AlertStatusSection, string> = {
  needAttention: 'Need attention',
  pending: 'Pending',
  history: 'History',
};

/**
 * Buckets alerts by their section, preserving input order within each bucket.
 * Every section is present, empty ones as `[]`.
 */
export function groupAlertsBySection<T extends { status: AlertRowStatus }>(
  alerts: readonly T[]
): Record<AlertStatusSection, T[]> {
  const grouped: Record<AlertStatusSection, T[]> = { needAttention: [], pending: [], history: [] };

  for (const alert of alerts) {
    grouped[ALERT_SECTION_BY_STATUS[alert.status]].push(alert);
  }

  return grouped;
}

/** Per-status alert totals, in the row model. */
export type AlertStatusCounts = {
  unread: number;
  read: number;
  pending: number;
  archived: number;
};

/**
 * Folds per-status totals into per-section header counts. Section headers show
 * the total for their status group, which exceeds the rows on screen wherever
 * the list is paginated.
 */
export function getAlertSectionCounts(counts: AlertStatusCounts): Record<AlertStatusSection, number> {
  return {
    needAttention: counts.unread + counts.read,
    pending: counts.pending,
    history: counts.archived,
  };
}

/**
 * A new `WorkItemStatus` does not compile until it picks a section — the twin
 * of {@link ALERT_SECTION_BY_STATUS} for the work-item-backed Center. The read
 * bit is not part of the fold: ACTIVE needs attention whether or not it has
 * been read, exactly as Unread and Read share a section above.
 */
export const WORK_ITEM_SECTION_BY_STATUS: Record<WorkItemStatus, AlertStatusSection> = {
  [WorkItemStatus.Active]: 'needAttention',
  [WorkItemStatus.Pending]: 'pending',
  [WorkItemStatus.Archived]: 'history',
};

/**
 * Buckets work items by their section, preserving input order within each
 * bucket. Every section is present, empty ones as `[]`. Runs on raw fragment
 * rows — the fold keys on `WorkItemStatus`, so it happens before any adapter
 * reshapes a row for rendering.
 */
export function groupWorkItemsBySection<T extends { status: WorkItemStatus }>(
  workItems: readonly T[]
): Record<AlertStatusSection, T[]> {
  const grouped: Record<AlertStatusSection, T[]> = { needAttention: [], pending: [], history: [] };

  for (const workItem of workItems) {
    grouped[WORK_ITEM_SECTION_BY_STATUS[workItem.status]].push(workItem);
  }

  return grouped;
}

/** Per-status work-item totals, as returned by `useWorkItemStatusCounts`. */
export type WorkItemStatusCounts = GetWorkItemStatusCountsQuery['getWorkItemStatusCounts'];

/**
 * Folds per-status work-item totals into per-section header counts — the twin
 * of {@link getAlertSectionCounts}. `unread` is the bell's number, not a
 * section's: it is a subset of `active`, which needAttention already counts.
 */
export function getWorkItemSectionCounts(counts: WorkItemStatusCounts): Record<AlertStatusSection, number> {
  return {
    needAttention: counts.active,
    pending: counts.pending,
    history: counts.archived,
  };
}

/** Intent an alert expresses, collapsed from the type enum's legacy and current members. */
export type AlertTypeBucket = 'update' | 'reminder' | 'general';

/** A new `AlertRowType` does not compile until it picks a bucket. */
export const ALERT_TYPE_BUCKET_BY_TYPE: Record<AlertRowType, AlertTypeBucket> = {
  REMINDER: 'reminder',
  CONFIG: 'general',
  UPDATE: 'update',
};

/**
 * The entity references an alert can carry — the work-item fragment's nested
 * shapes, with the plural relations flattened to their lead row the way
 * `workItemToListedAlert` flattens them.
 */
export type AlertEntityRefs = {
  transaction?: WorkItemDetailsFragment['transactions'][number] | null;
  merchant?: WorkItemDetailsFragment['merchants'][number] | null;
  financialAccount?: WorkItemDetailsFragment['financialAccount'];
  bankConnection?: WorkItemDetailsFragment['bankConnection'];
  bill?: WorkItemDetailsFragment['bills'][number] | null;
  recurringTransaction?: WorkItemDetailsFragment['recurringTransaction'];
};

/** Name and logo of the entity an alert is about, for the leading logo tile. */
export interface IAlertEntity {
  name: string;
  logoUrl: string | null;
}

const toEntity = (name: string, logoUrl?: string | null, logoBase64Encoding?: string | null): IAlertEntity => ({
  name,
  logoUrl: bankLogoSrc({ logoUrl, logoBase64Encoding }),
});

type AlertMerchantRef =
  | NonNullable<AlertEntityRefs['merchant']>
  | NonNullable<NonNullable<AlertEntityRefs['transaction']>['merchant']>
  | NonNullable<NonNullable<AlertEntityRefs['bill']>['merchant']>
  | NonNullable<NonNullable<AlertEntityRefs['recurringTransaction']>['merchant']>;

type AlertFinancialAccountRef =
  | NonNullable<AlertEntityRefs['financialAccount']>
  | NonNullable<NonNullable<AlertEntityRefs['transaction']>['financialAccount']>;

type AlertBankRef = NonNullable<NonNullable<AlertEntityRefs['bankConnection']>['bank']>;

/**
 * The subject an alert is about, discriminated by how a surface draws it: a
 * merchant, a bill or stream that only names itself, an account, or a bank.
 */
export type AlertSubject =
  | { kind: 'merchant'; merchant: AlertMerchantRef }
  | { kind: 'named'; name: string }
  | { kind: 'financialAccount'; financialAccount: AlertFinancialAccountRef }
  | { kind: 'bank'; bank: AlertBankRef };

/**
 * The subject an alert is about, or `null` when it is about the household as a
 * whole. Merchants win over accounts because an alert naming both is about the
 * spend, not the card it landed on.
 */
export function getAlertSubject(alert: AlertEntityRefs): AlertSubject | null {
  const merchant =
    alert.merchant ?? alert.transaction?.merchant ?? alert.bill?.merchant ?? alert.recurringTransaction?.merchant;
  if (merchant) {
    return { kind: 'merchant', merchant };
  }

  // A bill or stream with no merchant row still names itself, and that reads
  // closer to the alert than the account it settles from. A card statement is
  // the exception the shared helper folds in: its name *is* the card's.
  if (alert.bill) {
    return { kind: 'named', name: billDisplayName(alert.bill) };
  }
  if (alert.recurringTransaction) {
    return { kind: 'named', name: alert.recurringTransaction.description };
  }

  const financialAccount = alert.financialAccount ?? alert.transaction?.financialAccount;
  if (financialAccount) {
    return { kind: 'financialAccount', financialAccount };
  }

  const bank = alert.bankConnection?.bank;
  if (bank) {
    return { kind: 'bank', bank };
  }

  return null;
}

/** {@link getAlertSubject} folded to name + logo, for the leading logo tile. */
export function getAlertEntity(alert: AlertEntityRefs): IAlertEntity | null {
  const subject = getAlertSubject(alert);
  if (!subject) {
    return null;
  }

  switch (subject.kind) {
    case 'merchant':
      return toEntity(subject.merchant.name, subject.merchant.logoUrl);
    case 'named':
      return toEntity(subject.name);
    case 'financialAccount': {
      const { financialAccount } = subject;
      return toEntity(financialAccount.name, financialAccount.bank.logoUrl, financialAccount.bank.logoBase64Encoding);
    }
    case 'bank':
      return toEntity(subject.bank.name, subject.bank.logoUrl, subject.bank.logoBase64Encoding);
    default: {
      const _exhaustive: never = subject;
      return _exhaustive;
    }
  }
}

/** Which leading visual an alert gets — see `AlertTypeIcon`. */
export type AlertIconVariant = 'entity' | 'update' | 'reminder' | 'system';

/**
 * Picks an alert's leading visual. Only updates split on their subject: one
 * about a subject `getAlertEntity` can name shows that logo, and one about the
 * household shows the reviewed check instead.
 */
export function getAlertIconVariant(alert: AlertEntityRefs & { type: AlertRowType }): AlertIconVariant {
  const bucket = ALERT_TYPE_BUCKET_BY_TYPE[alert.type];

  if (bucket === 'reminder') {
    return 'reminder';
  }
  if (bucket === 'general') {
    return 'system';
  }

  return getAlertEntity(alert) ? 'entity' : 'update';
}
